import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GVSwitch from 'components/lib/GVSwitch/GVSwitch';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import { DocumentTypes } from 'types';
import { inspection, getSourceOverprint, getTargetOverprint, getIsOCR } from 'store';
import { SxProps, Theme } from '@mui/material/styles';
import styles from './OverprintSwitch.module.css';

interface OverprintSwitchProps {
  documentType: DocumentTypes;
  style?: React.CSSProperties;
}

const OverprintSwitch: React.FC<OverprintSwitchProps> = ({ documentType, style }) => {
  const dispatch = useDispatch();
  const enabled = useSelector(documentType === DocumentTypes.source ? getSourceOverprint : getTargetOverprint);
  const isOCR = useSelector(getIsOCR);
  const isDocumentOCR = documentType === DocumentTypes.source ? isOCR.sourceIsOCR : isOCR.targetIsOCR;

  const handleOverprintToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isDocumentOCR) return;

    const newEnabled = event.target.checked;
    dispatch(inspection.actions.setOverprint({ documentType, enabled: newEnabled }));

    const viewer = PDFManagerFactory.getViewer(documentType);
    const doc = viewer?.instance?.Core?.documentViewer?.getDocument();
    const OverprintPreviewMode = viewer?.instance?.Core?.OverprintPreviewMode;
    if (doc && OverprintPreviewMode) {
      doc.setOverprintPreviewMode(newEnabled ? OverprintPreviewMode.ON : OverprintPreviewMode.OFF);
      viewer?.instance?.Core?.documentViewer?.refreshAll();
      viewer?.instance?.Core?.documentViewer?.updateView();
    }
  };

  const switchStyles: SxProps<Theme> = isDocumentOCR
    ? {
        pointerEvents: 'none',
        '& .MuiSwitch-thumb': {
          backgroundColor: 'rgba(255, 255, 255, 0.38)',
        },
        // Override the unified GVSwitch checked-track red (which uses !important)
        // so a post-OCR document always renders the disabled gray state, even
        // when `enabled` is still true in the store.
        '& .MuiSwitch-track, & .Mui-checked + .MuiSwitch-track': {
          backgroundColor: 'rgba(255, 255, 255, 0.38) !important',
          borderColor: 'rgba(255, 255, 255, 0.38) !important',
        },
      }
    : {};

  const overprintSwitch = (
    <GVSwitch checked={enabled} onChange={handleOverprintToggle} sx={switchStyles} data-testid="overprint-switch" />
  );

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, ...style }}>
      <span
        style={{
          color: '#FFF',
          fontFeatureSettings: "'liga' off, 'clig' off",
          fontFamily: 'Open Sans',
          fontSize: 10,
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '16px',
        }}
      >
        Overprint
      </span>
      {isDocumentOCR ? (
        <GVTooltip
          title="Overprint cannot be enabled post-OCR"
          side="top"
          disableFocusListener
          disableHoverableContent
          className={styles.ocrTooltip}
        >
          <span>{overprintSwitch}</span>
        </GVTooltip>
      ) : (
        overprintSwitch
      )}
      <GVTooltip
        title={'Toggle overprint to "OFF" to help resolve visual issues caused by overlapping separations.'}
        side="top"
        disableFocusListener
        disableHoverableContent
        className={styles.tooltip}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            cursor: 'pointer',
          }}
        >
          <InfoOutlinedIcon style={{ fontSize: 12 }} data-testid="overprint-tooltip" />
        </span>
      </GVTooltip>
    </span>
  );
};

export default OverprintSwitch;
